import React from 'react';

export default function Receipt({ month, summary, budgets, debts }) {
  if (!month || !summary) return null;

  const monthName = new Date(0, month.month - 1).toLocaleString('default', { month: 'long' });
  const currentDate = new Date().toLocaleString('en-US', { 
    year: 'numeric', 
    month: '2-digit', 
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit'
  });

  const remainingPercent = summary.expected_income > 0 ? (summary.remaining / summary.expected_income) * 100 : 0;
  
  let receiptColor = "#ffffff";
  if (summary.remaining < 0) {
    receiptColor = "#fca5a5"; // red-300
  } else if (remainingPercent <= 10) {
    receiptColor = "#fecaca"; // red-200
  } else if (remainingPercent <= 30) {
    receiptColor = "#fed7aa"; // orange-200
  } else if (remainingPercent <= 60) {
    receiptColor = "#fef08a"; // yellow-200
  }

  return (
    <div 
      className="receipt-edges w-full max-w-sm mx-auto p-6 font-mono text-gray-800 text-sm mt-3 mb-3 transition-colors duration-300"
      style={{ '--receipt-bg': receiptColor }}
    >
      {/* Header */}
      <div className="text-center mb-6">
        <h2 className="text-xl font-bold uppercase tracking-widest mb-1">Katabo</h2>
        <p className="text-xs text-gray-500 uppercase tracking-wider">Monthly Summary</p>
        <p className="text-xs text-gray-400 mt-2">{currentDate}</p>
        <p className="text-xs text-gray-400 uppercase">Period: {monthName} {month.year}</p>
      </div>

      <div className="border-b-2 border-dashed border-gray-300 mb-4"></div>

      {/* Income Section */}
      <div className="mb-4">
        <div className="flex justify-between font-bold mb-2">
          <span>EXPECTED INCOME</span>
          <span>K{summary.expected_income}</span>
        </div>
      </div>

      <div className="border-b-2 border-dashed border-gray-300 mb-4"></div>

      {/* Commitments Section */}
      <div className="mb-4 space-y-1">
        <p className="font-bold mb-2 text-xs">COMMITMENTS</p>
        
        {Number(month.rent_amount) > 0 && (
          <div className="flex justify-between items-start">
            <span className="flex-1 pr-2">RENT</span>
            <span>K{month.rent_amount}</span>
          </div>
        )}

        {budgets.map(b => (
          <div key={`b-${b.id}`} className="flex justify-between items-start">
            <span className="flex-1 pr-2 uppercase truncate">{b.name}</span>
            <span>K{b.amount}</span>
          </div>
        ))}

        {debts.map(d => (
          <div key={`d-${d.id}`} className="flex justify-between items-start text-gray-600">
            <span className="flex-1 pr-2 uppercase truncate">{d.name} {d.status === 'Paid' ? '(PAID)' : ''}</span>
            <span className={d.status === 'Paid' ? 'line-through' : ''}>K{d.amount}</span>
          </div>
        ))}

        {budgets.length === 0 && debts.length === 0 && Number(month.rent_amount) === 0 && (
          <p className="text-gray-400 italic text-center py-2 text-xs">No commitments</p>
        )}
      </div>

      <div className="border-b border-gray-300 mb-2"></div>

      {/* Totals */}
      <div className="flex justify-between font-bold mb-4">
        <span>TOTAL DEDUCTIONS</span>
        <span>K{summary.committed}</span>
      </div>

      <div className="border-b-2 border-dashed border-gray-300 mb-4"></div>

      {/* Remaining */}
      <div className="flex justify-between items-center bg-gray-200 -mx-6 px-6 py-3 mb-6 font-bold text-lg">
        <span>REMAINING</span>
        <span className={summary.remaining < 0 ? 'text-red-600' : ''}>
          K{summary.remaining}
        </span>
      </div>

      {/* Footer */}
      <div className="text-center text-xs text-gray-500 uppercase tracking-widest space-y-1">
        <p>Thank you for planning</p>
        <p>with Katabo!</p>
        {/* Fake barcode for aesthetics */}
        <div className="mt-4 font-barcode text-4xl text-gray-400 tracking-[-2px] overflow-hidden whitespace-nowrap">
          ||| || | | |||| || | | | ||| || | 
        </div>
      </div>
    </div>
  );
}
